import { useEffect, useState } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { getCollectionSites } from '../services/collection-site.service';
import { getDashboardByBranch } from '../services/dashboard.service';
import type { DashboardByBranchResponse } from '../types/dashboard.type';
import { getTruckTracking, listRouteRunsForDay } from '../services/fleet.service';
import { listPickupOrdersForMonth } from '../services/pickupOrder.service';
import type { CollectionSiteSchema } from '../types/collection-site.type';
import type { OpsRouteRun, TruckTrackingItem } from '../types/fleet.type';
import type { MonthPickupOrders } from '../types/pickupOrder.type';
import { localDateKey } from '../utils/serverTime';

/** `forbidden` hides a section (missing permission is not an error). */
export type OperationsQueryStatus = 'idle' | 'loading' | 'ok' | 'forbidden' | 'error';

export interface OperationsQuery<T> {
  status: OperationsQueryStatus;
  data: T;
  /** When the browser last received this data (ms epoch) — the honest freshness basis. */
  fetchedAt: number | null;
  isFetching: boolean;
  refetch: () => void;
}

function httpStatus(error: unknown): number | undefined {
  return (error as { response?: { status?: number } } | null)?.response?.status;
}

const NO_RETRY = new Set([400, 401, 403, 404, 422]);

function retry(failureCount: number, error: unknown): boolean {
  const status = httpStatus(error);
  return !(status !== undefined && NO_RETRY.has(status)) && failureCount < 2;
}

function toOperationsQuery<T>(query: UseQueryResult<T, unknown>, enabled: boolean, empty: T): OperationsQuery<T> {
  let status: OperationsQueryStatus;
  if (!enabled) status = 'idle';
  else if (query.isError) status = httpStatus(query.error) === 403 ? 'forbidden' : 'error';
  else if (query.isPending) status = 'loading';
  else status = 'ok';
  return {
    status,
    data: query.data ?? empty,
    fetchedAt: query.dataUpdatedAt ? query.dataUpdatedAt : null,
    isFetching: query.isFetching,
    refetch: () => {
      void query.refetch();
    },
  };
}

const NO_TRUCKS: TruckTrackingItem[] = [];
const NO_RUNS: OpsRouteRun[] = [];
const NO_SITES: CollectionSiteSchema[] = [];

/**
 * Latest reported truck positions. Refreshed every 30 s while the tab is
 * visible (react-query pauses interval refetches in background tabs).
 */
export function useTruckTracking(orgId: string | null | undefined, enabled: boolean): OperationsQuery<TruckTrackingItem[]> {
  const on = Boolean(orgId) && enabled;
  const query = useQuery({
    queryKey: ['zero', 'truck-tracking', orgId],
    queryFn: () => getTruckTracking(orgId as string),
    enabled: on,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    retry,
  });
  return toOperationsQuery(query, on, NO_TRUCKS);
}

/**
 * Route runs for one day. Today's runs refresh every 60 s while any run is
 * still pending or in progress; past days are fetched once.
 */
export function useRouteRuns(
  orgId: string | null | undefined,
  runDate: string,
  enabled: boolean,
): OperationsQuery<OpsRouteRun[]> {
  const on = Boolean(orgId) && enabled;
  const isToday = runDate === localDateKey(new Date());
  const query = useQuery({
    queryKey: ['zero', 'route-runs', orgId, runDate],
    queryFn: () => listRouteRunsForDay(orgId as string, runDate),
    enabled: on,
    refetchInterval: (q) =>
      isToday && (q.state.data ?? []).some((r) => r.status === 'in_progress' || r.status === 'pending') ? 60_000 : false,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: isToday,
    staleTime: isToday ? 0 : 5 * 60_000,
    retry,
  });
  return toOperationsQuery(query, on, NO_RUNS);
}

/** Collection sites (branches) with coordinates — positions are registered, not tracked. */
export function useCollectionSites(orgId: string | null | undefined, enabled: boolean): OperationsQuery<CollectionSiteSchema[]> {
  const on = Boolean(orgId) && enabled;
  const query = useQuery({
    queryKey: ['zero', 'collection-sites', orgId],
    queryFn: async () => {
      const res = (await getCollectionSites({ organization_id: orgId as string })) as
        | { result?: CollectionSiteSchema[] }
        | CollectionSiteSchema[];
      if (Array.isArray(res)) return res;
      return Array.isArray(res?.result) ? res.result : [];
    },
    enabled: on,
    staleTime: 10 * 60_000,
    retry,
  });
  return toOperationsQuery(query, on, NO_SITES);
}

/**
 * Month metrics per branch (the ESG by-branch endpoint, `esg_report.read`).
 * Loaded only when a branch view is opened.
 */
export function useBranchMetrics(
  orgId: string | null | undefined,
  monthValue: string,
  enabled: boolean,
): OperationsQuery<DashboardByBranchResponse | null> {
  const on = Boolean(orgId) && enabled;
  const query = useQuery({
    queryKey: ['zero', 'branch-metrics', orgId, monthValue],
    queryFn: () => getDashboardByBranch(orgId as string, monthValue),
    enabled: on,
    staleTime: 5 * 60_000,
    retry,
  });
  return toOperationsQuery<DashboardByBranchResponse | null>(
    query as UseQueryResult<DashboardByBranchResponse | null, unknown>,
    on,
    null,
  );
}

const NO_ORDERS: MonthPickupOrders = { orders: [], total: 0, truncated: false };

/**
 * The month's pickup orders (`pickup_order.list`) for the per-material daily
 * split. Loaded only when the split is opened; `syncKey` (the dashboard's
 * fetch time) refetches it whenever the dashboard totals are refreshed so
 * both views describe the same moment.
 */
export function useMonthPickupOrders(
  orgId: string | null | undefined,
  monthStart: Date | null,
  syncKey: number | null,
  enabled: boolean,
): OperationsQuery<MonthPickupOrders> {
  const on = Boolean(orgId) && enabled;
  const monthKey = monthStart ? localDateKey(monthStart) : 'all-time';
  const query = useQuery({
    queryKey: ['zero', 'month-pickup-orders', orgId, monthKey, syncKey],
    queryFn: () => listPickupOrdersForMonth(orgId as string, monthStart),
    enabled: on,
    staleTime: 5 * 60_000,
    // Keep the split on screen while a refresh of the same month loads.
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === orgId && previousQuery?.queryKey[3] === monthKey ? previous : undefined,
    retry,
  });
  return toOperationsQuery(query, on, NO_ORDERS);
}

/** Current time that ticks every `intervalMs` while the tab is visible (for "3 min ago"). */
export function useNow(intervalMs = 15_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const tick = () => {
      if (typeof document === 'undefined' || document.visibilityState === 'visible') setNow(Date.now());
    };
    const id = window.setInterval(tick, intervalMs);
    document.addEventListener('visibilitychange', tick);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [intervalMs]);
  return now;
}
